import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import PhoneInputWithSearch from '../components/PhoneInputWithSearch';
import { 
    ChevronRight, Star, X, Phone, Lock, User, Mail, Globe,
    Mountain, Waves, TreePine, Heart, Camera, Compass,
    MapPin, Calendar, Users, ArrowRight, Search, Menu
} from 'lucide-react';

// Safari video - multiple sources for reliability
const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_0575a6a2-b345-46db-a92e-3f718ea0ef25/artifacts/gyn9tgie_Tanzania_Country_Video_3MINUTES_VIDEO_720P%20%281%29.mp4";
const HERO_POSTER_URL = "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1920";

// Nationality options
const nationalities = [
    { code: 'US', name: 'United States' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'CA', name: 'Canada' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'AT', name: 'Austria' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'IE', name: 'Ireland' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'KE', name: 'Kenya' },
    { code: 'TZ', name: 'Tanzania' },
    { code: 'IN', name: 'India' },
    { code: 'CN', name: 'China' },
    { code: 'JP', name: 'Japan' },
    { code: 'KR', name: 'South Korea' },
    { code: 'SG', name: 'Singapore' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'BR', name: 'Brazil' },
    { code: 'MX', name: 'Mexico' },
    { code: 'AR', name: 'Argentina' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'GH', name: 'Ghana' },
    { code: 'ET', name: 'Ethiopia' },
    { code: 'UG', name: 'Uganda' },
    { code: 'RW', name: 'Rwanda' },
    { code: 'EG', name: 'Egypt' },
    { code: 'MA', name: 'Morocco' },
    { code: 'IL', name: 'Israel' },
    { code: 'TR', name: 'Turkey' },
    { code: 'RU', name: 'Russia' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'TH', name: 'Thailand' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'PH', name: 'Philippines' },
    { code: 'OTHER', name: 'Other Country' }
];

const experiences = [
    {
        title: "Wildlife Safari",
        subtitle: "Big Five & Great Migration",
        description: "From Serengeti to Ngorongoro - Big Five encounters and the Great Migration await.",
        image: "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800",
        category: "WILDLIFE"
    },
    {
        title: "Mount Kilimanjaro",
        subtitle: "Africa's Highest Peak",
        description: "Summit Africa's roof through five distinct climate zones. An adventure of a lifetime.",
        image: "https://images.unsplash.com/photo-1621414050946-1b936a78491f?w=800",
        category: "ADVENTURE"
    },
    {
        title: "Zanzibar Beaches",
        subtitle: "Spice Island Paradise",
        description: "Powder-white sands, turquoise waters, and swaying palms. Perfect for unforgettable sunsets.",
        image: "https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=800",
        category: "BEACHES"
    },
    {
        title: "Cultural Experiences",
        subtitle: "Meet the Maasai",
        description: "Meet Maasai, Chagga and more - crafts, music, food tours and authentic hospitality.",
        image: "https://images.unsplash.com/photo-1630067458414-0080622bc0df?w=800",
        category: "CULTURE"
    }
];

const destinations = [
    { name: "Serengeti National Park", tours: "120+ Tours", image: "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=600" },
    { name: "Ngorongoro Crater", tours: "85+ Tours", image: "https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?w=600" },
    { name: "Zanzibar Island", tours: "90+ Tours", image: "https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600" },
    { name: "Mount Kilimanjaro", tours: "45+ Routes", image: "https://images.unsplash.com/photo-1621414050946-1b936a78491f?w=600" },
    { name: "Tarangire Park", tours: "60+ Tours", image: "https://images.unsplash.com/photo-1547970810-dc1eac37d174?w=600" },
    { name: "Lake Manyara", tours: "40+ Tours", image: "https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=600" }
];

const stats = [
    { value: "500+", label: "Verified Operators" },
    { value: "10,000+", label: "Happy Travelers" },
    { value: "50+", label: "Destinations" },
    { value: "4.9", label: "Average Rating", icon: Star }
];

export default function Landing() {
    const [showSignIn, setShowSignIn] = useState(false);
    const [isSignUp, setIsSignUp] = useState(false);
    const [loading, setLoading] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    // Login form
    const [loginPhone, setLoginPhone] = useState('');
    const [loginPin, setLoginPin] = useState('');
    
    // Signup form
    const [signupPhone, setSignupPhone] = useState('');
    const [signupEmail, setSignupEmail] = useState('');
    const [signupNationality, setSignupNationality] = useState('');
    const [signupPin, setSignupPin] = useState('');
    const [nationalitySearch, setNationalitySearch] = useState('');
    
    const { login, register, isAuthenticated, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const getDashboardLink = () => {
        if (!user) return '/auth';
        switch (user.role) {
            case 'admin': return '/admin';
            case 'operator': return '/operator';
            default: return '/dashboard';
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const userData = await login(loginPhone, loginPin);
            toast.success('Welcome back!');
            setShowSignIn(false);
            const hasSnapshot = sessionStorage.getItem('tripWizardSnapshot');
            if (hasSnapshot && userData.role === 'traveler') { navigate('/create-trip'); return; }
            if (userData.role === 'admin') navigate('/admin');
            else if (userData.role === 'operator') navigate('/operator');
            else navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(signupPhone, signupEmail.split('@')[0] || 'Traveler', signupPin);
            toast.success('Welcome to safarinodes!');
            setShowSignIn(false);
            const hasSnapshot = sessionStorage.getItem('tripWizardSnapshot');
            if (hasSnapshot) { navigate('/create-trip'); return; }
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    const openSignIn = () => {
        setIsSignUp(false);
        setShowSignIn(true);
    };

    const filteredNationalities = nationalities.filter(n => 
        n.name.toLowerCase().includes(nationalitySearch.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation - Fixed header */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#073763] shadow-sm">
                <div className="flex items-center justify-between">
                    {/* Left - Brand */}
                    <div className="px-4 sm:px-6 py-4 flex items-center gap-3">
                        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-1.5 text-white/80 hover:text-white" data-testid="mobile-menu-toggle">
                            <Menu size={22} />
                        </button>
                        <Link to="/" className="flex items-center" data-testid="brand-logo">
                            <img src="/safarinodes-logo.png" alt="safarinodes" className="h-9 sm:h-10 w-auto" />
                        </Link>
                    </div>

                    {/* Center - Nav Links */}
                    <div className="hidden md:flex items-center gap-8">
                        <Link 
                            to="/destinations" 
                            className="font-medium text-white/90 hover:text-white transition-colors"
                            data-testid="nav-discover"
                        >
                            Discover Tanzania
                        </Link>
                        <Link 
                            to="/experiences" 
                            className="font-medium text-white/90 hover:text-white transition-colors"
                            data-testid="nav-experiences"
                        >
                            Explore Experiences
                        </Link>
                    </div>

                    {/* Right - Sign In */}
                    <div className="bg-white/20 backdrop-blur-sm border border-white/30 px-4 sm:px-8 py-2.5 sm:py-3 mr-3 sm:mr-4 my-2 rounded-full flex items-center">
                        {isAuthenticated ? (
                            <button
                                onClick={() => navigate(getDashboardLink())}
                                className="text-white text-sm sm:text-base font-medium hover:text-white/80 transition-colors"
                                data-testid="nav-dashboard"
                            >
                                Dashboard
                            </button>
                        ) : (
                            <button
                                onClick={openSignIn}
                                className="text-white text-sm sm:text-base font-medium hover:text-white/80 transition-colors"
                                data-testid="nav-signin"
                            >
                                Sign in
                            </button>
                        )}
                    </div>
                </div>
                {/* Mobile Nav Dropdown */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-white/20 bg-[#073763] px-4 py-3 space-y-1" data-testid="mobile-nav-menu">
                        <Link to="/destinations" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                            Discover Tanzania
                        </Link>
                        <Link to="/experiences" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                            Explore Experiences
                        </Link>
                        <Link to="/operator/register" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                            Join as Operator
                        </Link>
                    </div>
                )}
            </nav>

            {/* Hero Section with Video */}
            <section className="video-hero">
                <video 
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                    poster={HERO_POSTER_URL}
                    className="video-bg"
                >
                    <source src={HERO_VIDEO_URL} type="video/mp4" />
                </video>
                <div className="video-overlay" />
                
                <div className="video-content min-h-screen flex items-center">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-32 w-full">
                        <div className="max-w-3xl animate-fade-in-up">
                            <p className="text-white/80 text-sm sm:text-lg font-medium mb-3 sm:mb-4 tracking-wide uppercase">
                                Welcome to Tanzania
                            </p>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Where Memories Begin
                            </h1>
                            <p className="text-base sm:text-lg text-white/80 mb-8 sm:mb-10 max-w-xl leading-relaxed">
                                Connect with verified Tanzanian tour operators who craft personalized itineraries just for you. From Serengeti sunsets to Zanzibar beaches.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Button 
                                    onClick={() => navigate('/trip/create')}
                                    className="bg-white text-[#073763] hover:bg-gray-100 text-base px-8 py-6 rounded-full font-semibold transition-all hover:scale-105"
                                    data-testid="hero-start-btn"
                                >
                                    Start Planning Your Trip
                                    <ChevronRight className="ml-2" size={20} />
                                </Button>
                                <Button 
                                    onClick={() => navigate('/destinations')}
                                    className="btn-outline-white text-base px-8 py-6"
                                    data-testid="hero-explore-btn"
                                >
                                    Explore Destinations
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Bar */}
                <div className="absolute bottom-0 left-0 right-0 bg-[#073763] z-10">
                    <div className="max-w-7xl mx-auto px-6 py-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            {stats.map((stat, idx) => (
                                <div key={idx} className="text-center">
                                    <p className="text-2xl md:text-3xl font-bold text-white flex items-center justify-center gap-2">
                                        {stat.value}
                                        {stat.icon && <stat.icon className="text-yellow-400" size={20} fill="currentColor" />}
                                    </p>
                                    <p className="text-white/60 text-sm mt-1">{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Top Experiences Section */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <p className="text-[#073763] font-medium mb-2 uppercase tracking-wide text-sm">Curated For You</p>
                        <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A]">
                            Top Experiences in Tanzania
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {experiences.map((exp, idx) => (
                            <div 
                                key={idx}
                                className="experience-card aspect-[3/4] group"
                                onClick={() => navigate('/experiences')}
                                data-testid={`experience-card-${idx}`}
                            >
                                <img 
                                    src={exp.image}
                                    alt={exp.title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="experience-card-overlay">
                                    <span className="text-white/60 text-xs font-semibold tracking-wider mb-2">{exp.category}</span>
                                    <h3 className="text-xl font-bold text-white mb-1">{exp.title}</h3>
                                    <p className="text-white/70 text-sm">{exp.subtitle}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <p className="text-[#073763] font-medium mb-2 uppercase tracking-wide text-sm">Simple & Seamless</p>
                        <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A]">
                            How It Works
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { 
                                step: "1",
                                icon: Compass, 
                                title: "Share Your Vision", 
                                desc: "Tell us your dream Tanzania trip - destinations, dates, interests, and budget." 
                            },
                            { 
                                step: "2",
                                icon: Users, 
                                title: "Get Matched", 
                                desc: "We connect you with verified local operators who specialize in your interests." 
                            },
                            { 
                                step: "3",
                                icon: Calendar, 
                                title: "Choose & Go", 
                                desc: "Compare itineraries, chat with operators, and book with confidence." 
                            }
                        ].map((item, idx) => (
                            <div key={idx} className="text-center">
                                <div className="w-16 h-16 rounded-full bg-[#073763] flex items-center justify-center mx-auto mb-6">
                                    <item.icon className="text-white" size={28} />
                                </div>
                                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                                <p className="text-gray-600">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Popular Destinations */}
            <section className="py-20 bg-[#073763]">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
                        <div>
                            <p className="text-white/60 font-medium mb-2 uppercase tracking-wide text-sm">Explore</p>
                            <h2 className="text-3xl md:text-4xl font-bold text-white">
                                Popular Destinations
                            </h2>
                        </div>
                        <Link 
                            to="/destinations" 
                            className="text-white/70 hover:text-white font-medium flex items-center gap-2 mt-4 md:mt-0 transition-colors"
                        >
                            View All <ArrowRight size={18} />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                        {destinations.map((dest, idx) => (
                            <div 
                                key={idx}
                                className="group relative rounded-xl overflow-hidden aspect-[4/3] cursor-pointer"
                                onClick={() => navigate('/destinations')}
                                data-testid={`destination-${idx}`}
                            >
                                <img 
                                    src={dest.image}
                                    alt={dest.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                <div className="absolute bottom-4 left-4">
                                    <h3 className="text-lg md:text-xl font-bold text-white mb-1">{dest.name}</h3>
                                    <p className="text-white/70 text-sm">{dest.tours}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6">
                        Ready to Explore Tanzania?
                    </h2>
                    <p className="text-gray-600 mb-10 max-w-2xl mx-auto text-lg">
                        Create your trip snapshot in minutes and receive personalized itineraries from Tanzania's best verified tour operators.
                    </p>
                    <Button 
                        onClick={() => navigate('/trip/create')}
                        className="btn-primary text-base px-10 py-6"
                        data-testid="cta-start-btn"
                    >
                        Start Planning Your Trip
                        <ChevronRight className="ml-2" size={20} />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12">
                        <div>
                            <div className="mb-4">
                                <img src="/safarinodes-logo.png" alt="safarinodes" className="h-9 w-auto" />
                            </div>
                            <p className="text-gray-400 text-sm">
                                Connecting travelers with verified Tanzanian tour operators for unforgettable experiences.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4">Explore</h4>
                            <ul className="space-y-2 text-sm text-gray-400">
                                <li><Link to="/destinations" className="hover:text-white transition-colors">Destinations</Link></li>
                                <li><Link to="/experiences" className="hover:text-white transition-colors">Experiences</Link></li>
                                <li><Link to="/trip/create" className="hover:text-white transition-colors">Plan a Trip</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4">For Operators</h4>
                            <ul className="space-y-2 text-sm text-gray-400">
                                <li><Link to="/operator/register" className="hover:text-white transition-colors">Join as Operator</Link></li>
                                <li><Link to="/auth" className="hover:text-white transition-colors">Operator Login</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4">Contact</h4>
                            <ul className="space-y-2 text-sm text-gray-400">
                                <li>hello@safarinodes.com</li>
                                <li>+255 123 456 789</li>
                                <li>Arusha, Tanzania</li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8 text-center text-sm text-gray-500">
                        <p>© 2026 safarinodes. All rights reserved.</p>
                    </div>
                </div>
            </footer>

            {/* Sign In / Sign Up Modal */}
            <Dialog open={showSignIn} onOpenChange={setShowSignIn}>
                <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden gap-0 [&>button]:text-white [&>button]:hover:text-white/80">
                    <DialogTitle className="sr-only">
                        {isSignUp ? 'Create Account' : 'Sign in'}
                    </DialogTitle>
                    {/* Header */}
                    <div className="auth-modal-header">
                        <h2 className="text-2xl font-bold text-white mb-2">
                            {isSignUp ? 'Create Account' : 'Sign in'}
                        </h2>
                        <p className="text-white/70 text-sm">
                            {isSignUp 
                                ? 'Join thousands of happy travelers' 
                                : 'safarinodes is way cooler when it knows who you are'}
                        </p>
                    </div>
                    
                    {/* Form Content */}
                    <div className="p-6">
                        {!isSignUp ? (
                            /* Sign In Form */
                            <form onSubmit={handleLogin} className="space-y-5">
                                <div>
                                    <Label className="form-label">Phone Number</Label>
                                    <PhoneInputWithSearch
                                        value={loginPhone}
                                        onChange={setLoginPhone}
                                        defaultCountry="TZ"
                                        data-testid="modal-login-phone"
                                    />
                                </div>
                                
                                <div>
                                    <Label className="form-label">PIN</Label>
                                    <Input
                                        type="password"
                                        placeholder="Enter your PIN"
                                        value={loginPin}
                                        onChange={(e) => setLoginPin(e.target.value)}
                                        className="input-karibu"
                                        maxLength={6}
                                        required
                                        data-testid="modal-login-pin"
                                    />
                                </div>
                                
                                <Button 
                                    type="submit" 
                                    className="btn-primary w-full py-6 text-base"
                                    disabled={loading}
                                    data-testid="modal-login-submit"
                                >
                                    {loading ? 'Signing in...' : 'Sign In'}
                                </Button>
                                
                                {/* Divider */}
                                <div className="auth-divider">
                                    <span>New to safarinodes?</span>
                                </div>
                                
                                {/* Sign Up Options */}
                                <Button 
                                    type="button"
                                    onClick={() => setIsSignUp(true)}
                                    className="w-full py-6 text-base bg-white border-2 border-[#073763] text-[#073763] hover:bg-[#073763] hover:text-white rounded-full transition-all"
                                    data-testid="switch-to-signup"
                                >
                                    Create a Traveler Account
                                </Button>
                                
                                <p className="text-center text-sm text-gray-500">
                                    <Link 
                                        to="/operator/register"
                                        onClick={() => setShowSignIn(false)}
                                        className="text-[#073763] hover:underline font-medium"
                                    >
                                        Register as a Tour Operator
                                    </Link>
                                </p>
                            </form>
                        ) : (
                            /* Sign Up Form */
                            <form onSubmit={handleSignup} className="space-y-4">
                                <div>
                                    <Label className="form-label">Phone Number</Label>
                                    <PhoneInputWithSearch
                                        value={signupPhone}
                                        onChange={setSignupPhone}
                                        defaultCountry="US"
                                        data-testid="modal-signup-phone"
                                    />
                                </div>
                                
                                <div>
                                    <Label className="form-label">Email Address</Label>
                                    <Input
                                        type="email"
                                        placeholder="your@email.com"
                                        value={signupEmail}
                                        onChange={(e) => setSignupEmail(e.target.value)}
                                        className="input-karibu"
                                        required
                                        data-testid="modal-signup-email"
                                    />
                                </div>
                                
                                <div>
                                    <Label className="form-label">Nationality</Label>
                                    <Select value={signupNationality} onValueChange={setSignupNationality}>
                                        <SelectTrigger className="input-karibu" data-testid="modal-signup-nationality">
                                            <SelectValue placeholder="Select your nationality" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[300px]">
                                            <div className="px-3 py-2 sticky top-0 bg-white border-b">
                                                <div className="relative">
                                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                                    <input
                                                        type="text"
                                                        placeholder="Search countries..."
                                                        value={nationalitySearch}
                                                        onChange={(e) => setNationalitySearch(e.target.value)}
                                                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#073763]"
                                                    />
                                                </div>
                                            </div>
                                            {filteredNationalities.map((nat) => (
                                                <SelectItem key={nat.code} value={nat.code}>
                                                    {nat.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                <div>
                                    <Label className="form-label">Create PIN (4-6 digits)</Label>
                                    <Input
                                        type="password"
                                        placeholder="Create a secure PIN"
                                        value={signupPin}
                                        onChange={(e) => setSignupPin(e.target.value)}
                                        className="input-karibu"
                                        maxLength={6}
                                        required
                                        data-testid="modal-signup-pin"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">You'll use this PIN to sign in</p>
                                </div>
                                
                                <Button 
                                    type="submit" 
                                    className="btn-primary w-full py-6 text-base"
                                    disabled={loading || !signupPhone || !signupEmail || !signupNationality || !signupPin}
                                    data-testid="modal-signup-submit"
                                >
                                    {loading ? 'Creating account...' : 'Create Account'}
                                </Button>
                                
                                {/* Back to Sign In */}
                                <p className="text-center text-sm text-gray-600">
                                    Already have an account?{' '}
                                    <button 
                                        type="button"
                                        onClick={() => setIsSignUp(false)}
                                        className="text-[#073763] hover:underline font-medium"
                                    >
                                        Sign in
                                    </button>
                                </p>
                            </form>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
